import { useState } from "react"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, CircularProgress, Stack, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { ErrorDisplay } from "../../../../../core/api/errorTypes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    CompanyAdminWorkgroupsQuery,
    fetchWorkgroupForCompany,
    WorkgroupForCompanyResponse,
} from "../../../../../modules/company/admin/organization/workgroups/workgroupsApi"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { MeetingServiceOrderWorkgroup } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"

import { SectionItemData } from "./common/ReceiptPrimitives"

const useStyles = makeStyles()((theme) => ({
    icon: {
        display: "inline-flex",
        color: theme.palette.tailwind.slate[400],
        cursor: "pointer",
        "@media print": {
            display: "none",
        },
    },
    name: {
        textAlign: "right",
    },
    tooltipLoader: {
        display: "flex",
        justifyContent: "center",
        padding: ".25rem",
    },
    memberList: {
        margin: 0,
        paddingLeft: "1rem",
    },
}))

const selectMembers = (data: WorkgroupForCompanyResponse) => data?.workgroup?.members ?? []

type WorkgroupInfoProps = Readonly<{
    workgroup: MeetingServiceOrderWorkgroup
}>

export function WorkgroupInfo({ workgroup }: WorkgroupInfoProps) {
    const { classes } = useStyles()
    const [isTooltipOpen, setIsTooltipOpen] = useState(false)

    const membersResult = useApiQuery(fetchWorkgroupForCompany, {
        queryName: CompanyAdminWorkgroupsQuery.DETAILS,
        dependencies: { workgroupId: String(workgroup.id) },
        enabled: isTooltipOpen,
        errorDisplay: ErrorDisplay.NONE,
        select: selectMembers,
    })

    const members = membersResult.data ?? []

    const tooltipContent = membersResult.isInitialLoading ? (
        <Box className={classes.tooltipLoader}>
            <CircularProgress size={16} color="inherit" />
        </Box>
    ) : members.length > 0 ? (
        <Stack gap=".25rem">
            <LocalizedStrict id="confirm-order-dialog-workgroup-members-title">
                <Typography variant="subtitle2">Workgroup members</Typography>
            </LocalizedStrict>
            <Typography variant="body2" component="ul" className={classes.memberList}>
                {members.map((member) => (
                    <li key={member.id}>{member.displayName}</li>
                ))}
            </Typography>
        </Stack>
    ) : (
        <LocalizedStrict id="confirm-order-dialog-workgroup-members-empty">
            <Typography variant="body2">No members to show</Typography>
        </LocalizedStrict>
    )

    return (
        <Stack direction="row" alignItems="center" gap=".375rem">
            <SectionItemData classNames={classes.name}>{workgroup.name}</SectionItemData>
            <Tooltip
                title={tooltipContent}
                arrow
                open={isTooltipOpen}
                onOpen={() => setIsTooltipOpen(true)}
                onClose={() => setIsTooltipOpen(false)}
            >
                <Box component="span" className={classes.icon}>
                    <FontAwesomeIcon icon={faCircleInfo} />
                </Box>
            </Tooltip>
        </Stack>
    )
}
